import { useState } from "react";
import { monthlyData, quarterlyData } from "../data/mockData";
import { MERCHANTS } from "../constants/merchants";

export default function DashboardPage({ products, promotions, user, t }) {
    const [revenueView, setRevenueView] = useState("monthly");
    const [activeMarket, setActiveMarket] = useState(null);

    // ── 商戶資料過濾 ────────────────────────────────────────
    const isAdmin = user.accountType === "admin";
    const userMerchant = isAdmin
        ? null
        : MERCHANTS.find((m) => m.id !== "developer" && user.email.includes(m.emailDomain));

    const allMarketData = [
        { name: "百佳 ParknShop", share: 38, revenue: 61940, growth: "+12.4%", status: "up", color: "#2a9d8f" },
        { name: "惠康 Wellcome", share: 27, revenue: 44010, growth: "+7.1%", status: "up", color: "#1a3a5c" },
        { name: "7-Eleven", share: 19, revenue: 30970, growth: "-2.3%", status: "down", color: "#e05c6a" },
        { name: "其他零售商", share: 16, revenue: 26080, growth: "+5.8%", status: "up", color: "#8ab0c4" },
    ];

    const marketData = isAdmin
        ? allMarketData
        : userMerchant
            ? allMarketData.filter((m) => {
                const merchantKey = userMerchant.name.split(" ")[0];
                return m.name.includes(merchantKey);
            })
            : [];

    const totalMarketRevenue = marketData.reduce((s, m) => s + m.revenue, 0);

    const chartData = revenueView === "monthly" ? monthlyData : quarterlyData;
    const maxRevenue = Math.max(...chartData.map((d) => d.revenue));
    const maxUsers = Math.max(...chartData.map((d) => d.users));

    const currentRevenue = monthlyData[5].revenue;
    const prevRevenue = monthlyData[4].revenue;
    const revenueGrowth = (((currentRevenue - prevRevenue) / prevRevenue) * 100).toFixed(1);
    const currentUsers = monthlyData[5].users;
    const prevUsers = monthlyData[4].users;
    const userGrowth = (((currentUsers - prevUsers) / prevUsers) * 100).toFixed(1);

    const kpis = [
        { label: "本月收益", value: `HK$${(currentRevenue / 1000).toFixed(0)}K`, icon: "💵", change: `${revenueGrowth > 0 ? "▲" : "▼"} ${Math.abs(revenueGrowth)}%`, positive: revenueGrowth > 0, sub: "vs 上月" },
        { label: "活躍用戶", value: currentUsers.toLocaleString(), icon: "👥", change: `${userGrowth > 0 ? "▲" : "▼"} ${Math.abs(userGrowth)}%`, positive: userGrowth > 0, sub: "vs 上月" },
        { label: "收益增長率", value: `${revenueGrowth}%`, icon: "📈", change: revenueGrowth > 0 ? "正增長" : "負增長", positive: revenueGrowth > 0, sub: "月環比" },
        { label: "用戶增長率", value: `${userGrowth}%`, icon: "🚀", change: userGrowth > 0 ? "正增長" : "負增長", positive: userGrowth > 0, sub: "月環比" },
        { label: "商品總數", value: products.length, icon: "🏷️", change: "已上架", positive: true, sub: "種商品" },
        { label: "進行中 Promo", value: promotions.length, icon: "🎯", change: "個推廣", positive: true, sub: "精準推薦" },
        { label: "季度收益", value: `HK$${(quarterlyData[1].revenue / 1000).toFixed(0)}K`, icon: "📊", change: "▲ 21.5%", positive: true, sub: "Q2 vs Q1" },
        { label: "合作零售商", value: marketData.length, icon: "🏪", change: "間合作", positive: true, sub: "零售夥伴" },
    ];

    const cardStyle = { background: t.surface, borderRadius: 14, padding: 24, border: `1px solid ${t.border}` };

    return (
        <div style={{ padding: 32 }}>
            <div style={{ marginBottom: 28 }}>
                <h2 style={{ fontSize: 24, fontWeight: 700, color: t.text, margin: "0 0 4px" }}>📊 Dashboard 概覽</h2>
                <p style={{ color: t.textFaint, fontSize: 13, margin: 0 }}>A-EYE Retail Management · 數據截至 Jun 2026</p>
            </div>

            {/* KPI Grid */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 14, marginBottom: 24 }}>
                {kpis.map((k, i) => (
                    <div key={i} style={{ ...cardStyle, padding: 18 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
                            <span style={{ fontSize: 22 }}>{k.icon}</span>
                            <span style={{ fontSize: 11, fontWeight: 700, padding: "3px 8px", borderRadius: 20, background: k.positive ? `${t.success}22` : `${t.danger}22`, color: k.positive ? t.success : t.danger }}>{k.change}</span>
                        </div>
                        <p style={{ fontSize: 24, fontWeight: 800, color: t.text, margin: "0 0 2px", letterSpacing: "-0.5px" }}>{k.value}</p>
                        <p style={{ color: t.textMuted, fontSize: 12, margin: "0 0 2px", fontWeight: 600 }}>{k.label}</p>
                        <p style={{ color: t.textFaint, fontSize: 11, margin: 0 }}>{k.sub}</p>
                    </div>
                ))}
            </div>

            {/* Revenue + Users Chart */}
            <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 16, marginBottom: 16 }}>
                <div style={cardStyle}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
                        <h3 style={{ color: t.text, fontSize: 16, fontWeight: 700, margin: 0 }}>💰 盈利圖表</h3>
                        <div style={{ display: "flex", gap: 6 }}>
                            {["monthly", "quarterly"].map((v) => (
                                <button key={v} onClick={() => setRevenueView(v)}
                                    style={{ padding: "5px 14px", borderRadius: 20, fontSize: 12, cursor: "pointer", fontWeight: 600, background: revenueView === v ? t.accent : t.inputBg, border: `1px solid ${revenueView === v ? t.accent : t.border}`, color: revenueView === v ? "white" : t.textMuted }}>
                                    {v === "monthly" ? "本月" : "季度"}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div style={{ display: "flex", alignItems: "flex-end", gap: 10, height: 180 }}>
                        {chartData.map((d, i) => (
                            <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
                                <span style={{ color: t.textFaint, fontSize: 10 }}>{(d.revenue / 1000).toFixed(0)}K</span>
                                <div style={{ width: "100%", display: "flex", gap: 2, alignItems: "flex-end", height: 150 }}>
                                    <div style={{ flex: 1, height: `${(d.revenue / maxRevenue) * 150}px`, background: `linear-gradient(180deg, ${t.accent} 0%, ${t.accent}aa 100%)`, borderRadius: "4px 4px 0 0" }} />
                                    <div style={{ flex: 1, height: `${(d.users / maxUsers) * 150}px`, background: `linear-gradient(180deg, #8ab0c4 0%, #8ab0c488 100%)`, borderRadius: "4px 4px 0 0" }} />
                                </div>
                                <span style={{ color: t.textFaint, fontSize: 11 }}>{d.label}</span>
                            </div>
                        ))}
                    </div>
                    <div style={{ display: "flex", gap: 20, marginTop: 14 }}>
                        {[{ color: t.accent, label: "收益 (HK$)" }, { color: "#8ab0c4", label: "用戶人數" }].map((l) => (
                            <div key={l.label} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                <div style={{ width: 12, height: 12, borderRadius: 3, background: l.color }} />
                                <span style={{ color: t.textMuted, fontSize: 12 }}>{l.label}</span>
                            </div>
                        ))}
                    </div>
                </div>

                <div style={cardStyle}>
                    <h3 style={{ color: t.text, fontSize: 16, fontWeight: 700, margin: "0 0 20px" }}>👥 用戶統計</h3>
                    {[
                        { label: "本月活躍用戶", value: currentUsers.toLocaleString(), bar: currentUsers / 4000, color: t.accent },
                        { label: "新增用戶", value: "412", bar: 412 / 1000, color: "#2a9d8f" },
                        { label: "回訪用戶", value: "2,568", bar: 2568 / 4000, color: "#8ab0c4" },
                        { label: "平均每日活躍", value: "99", bar: 99 / 150, color: "#4ecdc4" },
                    ].map((item) => (
                        <div key={item.label} style={{ marginBottom: 18 }}>
                            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                                <span style={{ color: t.textMuted, fontSize: 12 }}>{item.label}</span>
                                <span style={{ color: t.text, fontSize: 13, fontWeight: 700 }}>{item.value}</span>
                            </div>
                            <div style={{ background: t.inputBg, borderRadius: 4, height: 6, overflow: "hidden" }}>
                                <div style={{ height: "100%", borderRadius: 4, width: `${Math.min(item.bar * 100, 100)}%`, background: item.color }} />
                            </div>
                        </div>
                    ))}
                    <div style={{ marginTop: 8, padding: "12px 14px", background: t.inputBg, borderRadius: 10, border: `1px solid ${t.border}` }}>
                        <p style={{ color: t.textFaint, fontSize: 11, margin: "0 0 8px", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px" }}>增長率摘要</p>
                        <div style={{ display: "flex", justifyContent: "space-between" }}>
                            {[
                                { label: "收益增長", value: `+${revenueGrowth}%`, positive: true },
                                { label: "用戶增長", value: `${userGrowth > 0 ? "+" : ""}${userGrowth}%`, positive: userGrowth > 0 },
                                { label: "季度增長", value: "+21.5%", positive: true },
                            ].map((g, i, arr) => (
                                <div key={g.label} style={{ textAlign: "center", flex: 1, borderRight: i < arr.length - 1 ? `1px solid ${t.border}` : "none" }}>
                                    <p style={{ color: g.positive ? t.success : t.danger, fontSize: 18, fontWeight: 800, margin: 0 }}>{g.value}</p>
                                    <p style={{ color: t.textFaint, fontSize: 10, margin: 0 }}>{g.label}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* Market Result */}
            <div style={cardStyle}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
                    <h3 style={{ color: t.text, fontSize: 16, fontWeight: 700, margin: 0 }}>🏪 市場結果分析（Market Result）</h3>
                    {!isAdmin && userMerchant && (
                        <span style={{ color: t.success, fontSize: 11, padding: "3px 10px", background: `${t.success}18`, borderRadius: 20, border: `1px solid ${t.success}44` }}>
                            🔒 僅顯示 {userMerchant.name} 資料
                        </span>
                    )}
                    <span style={{ color: t.textFaint, fontSize: 12 }}>本月總收益：HK${(totalMarketRevenue / 1000).toFixed(0)}K</span>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: isAdmin ? "1fr 1fr 1fr" : "1fr 1fr", gap: 20 }}>

                    {/* Bar List */}
                    <div>
                        <p style={{ color: t.textMuted, fontSize: 13, fontWeight: 600, marginBottom: 14 }}>各零售商收益佔比</p>
                        {marketData.map((m) => (
                            <div key={m.name}
                                onClick={() => setActiveMarket(activeMarket?.name === m.name ? null : m)}
                                style={{ marginBottom: 14, cursor: "pointer", padding: "10px 12px", borderRadius: 8, border: `1px solid ${activeMarket?.name === m.name ? m.color : "transparent"}`, background: activeMarket?.name === m.name ? `${m.color}15` : "transparent", transition: "all 0.2s" }}>
                                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                        <div style={{ width: 10, height: 10, borderRadius: "50%", background: m.color }} />
                                        <span style={{ color: t.text, fontSize: 13, fontWeight: 600 }}>{m.name}</span>
                                    </div>
                                    <div style={{ display: "flex", gap: 12 }}>
                                        <span style={{ color: t.textMuted, fontSize: 12 }}>HK${(m.revenue / 1000).toFixed(0)}K</span>
                                        <span style={{ fontSize: 12, fontWeight: 700, color: m.status === "up" ? t.success : t.danger }}>{m.growth}</span>
                                    </div>
                                </div>
                                <div style={{ background: t.inputBg, borderRadius: 4, height: 8, overflow: "hidden" }}>
                                    <div style={{ height: "100%", borderRadius: 4, width: `${m.share}%`, background: m.color }} />
                                </div>
                                <span style={{ color: t.textFaint, fontSize: 11, marginTop: 3, display: "block" }}>市場佔比 {m.share}%</span>
                            </div>
                        ))}
                    </div>

                    {/* Pie Chart */}
                    <div>
                        <p style={{ color: t.textMuted, fontSize: 13, fontWeight: 600, marginBottom: 14 }}>市場份額分佈</p>
                        <div style={{ display: "flex", alignItems: "center", gap: 20 }}>
                            <svg width="140" height="140" viewBox="0 0 140 140">
                                {(() => {
                                    let cumulative = 0;
                                    return marketData.map((m, i) => {
                                        const startAngle = (cumulative / 100) * 360 - 90;
                                        cumulative += m.share;
                                        const endAngle = (cumulative / 100) * 360 - 90;
                                        const r = 50, cx = 70, cy = 70;
                                        const toRad = (deg) => (deg * Math.PI) / 180;
                                        const x1 = cx + r * Math.cos(toRad(startAngle)), y1 = cy + r * Math.sin(toRad(startAngle));
                                        const x2 = cx + r * Math.cos(toRad(endAngle)), y2 = cy + r * Math.sin(toRad(endAngle));
                                        return (
                                            <path key={i}
                                                d={`M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${m.share > 50 ? 1 : 0} 1 ${x2} ${y2} Z`}
                                                fill={m.color} opacity={activeMarket?.name === m.name ? 1 : 0.75}
                                                stroke={t.surface} strokeWidth={2} style={{ cursor: "pointer" }}
                                                onClick={() => setActiveMarket(activeMarket?.name === m.name ? null : m)} />
                                        );
                                    });
                                })()}
                                <circle cx="70" cy="70" r="28" fill={t.surface} />
                                <text x="70" y="66" textAnchor="middle" fill={t.text} fontSize="11" fontWeight="700">{activeMarket ? `${activeMarket.share}%` : "市場"}</text>
                                <text x="70" y="80" textAnchor="middle" fill={t.textFaint} fontSize="9">{activeMarket ? activeMarket.name.slice(0, 4) : "份額"}</text>
                            </svg>
                            <div style={{ flex: 1 }}>
                                {activeMarket ? (
                                    <div style={{ padding: 14, background: t.inputBg, borderRadius: 10, border: `1px solid ${activeMarket.color}55` }}>
                                        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                                            <div style={{ width: 10, height: 10, borderRadius: "50%", background: activeMarket.color }} />
                                            <span style={{ color: t.text, fontSize: 13, fontWeight: 700 }}>{activeMarket.name}</span>
                                        </div>
                                        {[
                                            { label: "本月收益", value: `HK$${(activeMarket.revenue / 1000).toFixed(1)}K` },
                                            { label: "市場佔比", value: `${activeMarket.share}%` },
                                            { label: "增長率", value: activeMarket.growth },
                                            { label: "狀態", value: activeMarket.status === "up" ? "📈 增長中" : "📉 下跌中" },
                                        ].map((row) => (
                                            <div key={row.label} style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                                                <span style={{ color: t.textFaint, fontSize: 12 }}>{row.label}</span>
                                                <span style={{ color: row.label === "增長率" ? (activeMarket.status === "up" ? t.success : t.danger) : t.text, fontSize: 12, fontWeight: 600 }}>{row.value}</span>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div style={{ padding: 14, background: t.inputBg, borderRadius: 10, border: `1px solid ${t.border}` }}>
                                        <p style={{ color: t.textFaint, fontSize: 12, textAlign: "center", margin: 0 }}>點擊左側或圓餅圖<br />查看零售商詳情</p>
                                    </div>
                                )}
                                <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 4 }}>
                                    {marketData.map((m) => (
                                        <div key={m.name} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                            <div style={{ width: 8, height: 8, borderRadius: 2, background: m.color }} />
                                            <span style={{ color: t.textFaint, fontSize: 11 }}>{m.name} — {m.share}%</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Admin: All Merchants & Branches */}
                    {isAdmin && (
                        <div>
                            <p style={{ color: t.textMuted, fontSize: 13, fontWeight: 600, marginBottom: 14 }}>📋 所有商戶及分店</p>
                            <div style={{ maxHeight: 320, overflowY: "auto", display: "flex", flexDirection: "column", gap: 10 }}>
                                {MERCHANTS.filter((m) => m.id !== "developer").map((merchant) => (
                                    <div key={merchant.id} style={{ background: t.inputBg, borderRadius: 10, padding: "12px 14px", border: `1px solid ${merchant.color}44` }}>
                                        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                                            <span style={{ fontSize: 16 }}>{merchant.icon}</span>
                                            <span style={{ color: t.text, fontSize: 13, fontWeight: 700 }}>{merchant.name}</span>
                                            <span style={{ fontSize: 10, color: t.textFaint, marginLeft: "auto" }}>{merchant.regions.reduce((s, r) => s + r.branches.length, 0)} 間分店</span>
                                        </div>
                                        {merchant.regions.map((region) => (
                                            <div key={region.name} style={{ marginBottom: 8 }}>
                                                <p style={{ color: t.textFaint, fontSize: 10, fontWeight: 700, margin: "0 0 4px", textTransform: "uppercase", letterSpacing: "0.5px" }}>📍 {region.name}</p>
                                                {region.branches.map((branch) => (
                                                    <div key={branch.id} style={{ display: "flex", justifyContent: "space-between", padding: "3px 0", borderBottom: `1px solid ${t.border}33` }}>
                                                        <span style={{ color: t.text, fontSize: 11 }}>{branch.name}</span>
                                                        <span style={{ color: t.textFaint, fontSize: 10 }}>{branch.address}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        ))}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}